import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { routeSeo, SITE_URL } from '../content/seoRoutes';
import { AdBanner, InlineAd } from '../components/AdComponents';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { MapPin, CreditCard, Phone, Car, Train, Smartphone, Palette, Calendar, Trophy, Search } from "lucide-react";

export function HomePage() {
  const features = [
    {
      id: "/pin-codes",
      title: "PIN Codes",
      description: "Search Indian postal codes by city, district, or state with instant copy",
      icon: MapPin,
      color: "text-blue-600",
      count: "6 digit postal codes",
      path: "/pin-codes"
    },
    {
      id: "/ifsc-codes", 
      title: "IFSC Codes",
      description: "Find bank IFSC codes for NEFT, RTGS, and IMPS transfers with branch details",
      icon: CreditCard,
      color: "text-green-600",
      count: "Bank branch codes",
      path: "/ifsc-codes"
    },
    {
      id: "/std-codes",
      title: "STD Codes", 
      description: "Telephone area codes for cities across India with copy functionality",
      icon: Phone,
      color: "text-purple-600",
      count: "Landline codes",
      path: "/std-codes"
    },
    {
      id: "/rto-codes",
      title: "RTO Codes",
      description: "Vehicle registration codes for all Indian states with full names",
      icon: Car,
      color: "text-orange-600", 
      count: "Vehicle registration",
      path: "/rto-codes"
    },
    {
      id: "/railway-codes",
      title: "Railway Codes",
      description: "Station codes for Indian Railway booking and tracking with zone info",
      icon: Train,
      color: "text-red-600",
      count: "Station codes",
      path: "/railway-codes"
    },
    {
      id: "/ussd-codes",
      title: "USSD Codes",
      description: "Telecom operator codes for balance, recharge, and services by provider",
      icon: Smartphone, 
      color: "text-blue-600",
      count: "Telecom shortcuts",
      path: "/ussd-codes"
    },
    {
      id: "/html-colors",
      title: "HTML Colors & Symbols",
      description: "Color codes and Unicode symbols for web development with preview",
      icon: Palette,
      color: "text-pink-600",
      count: "Web development",
      path: "/html-colors"
    },
    {
      id: "/festivals", 
      title: "Festival Calendar",
      description: "Indian festivals and national holidays with dates and descriptions",
      icon: Calendar,
      color: "text-amber-600",
      count: "2025 Calendar",
      path: "/festivals"
    },
    {
      id: "/cricket",
      title: "Cricket Winners",
      description: "Cricket World Cup and IPL winners history with complete records",
      icon: Trophy,
      color: "text-yellow-600", 
      count: "Sports history",
      path: "/cricket"
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Indian Codes Hub",
    "description": "Complete database of Indian codes including PIN codes, IFSC codes, STD codes, and more",
    "url": SITE_URL
  };

  return (
    <>
      <SEOHead
        {...routeSeo('home')}
        keywords="Indian codes, PIN codes, IFSC codes, STD codes, RTO codes, railway codes, USSD codes, postal codes, bank codes"
        structuredData={structuredData}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Indian Codes Hub
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Your comprehensive destination for all Indian codes. Find PIN codes, IFSC codes, STD codes, 
            RTO codes, railway station codes, USSD codes, and more. Fast search with instant copy functionality 
            and up-to-date information.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-8 text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Always Updated</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>One-Click Copy</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span>Mobile Friendly</span>
            </div>
          </div>
        </div>

        {/* Top Banner Ad */}
        {/* <div className="mb-12 flex justify-center">
          <AdBanner size="leaderboard" />
        </div> */}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <Link
                key={feature.id}
                to={feature.path}
                className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-2xl"
              >
                <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-105 group-active:scale-95 group-active:shadow h-full rounded-2xl">
                  <CardHeader className="text-center pb-4">
                    <div className="mx-auto mb-4 p-4 bg-gray-50 rounded-full w-fit group-hover:bg-gray-100 group-active:bg-gray-100">
                      <Icon className={`h-8 w-8 ${feature.color}`} />
                    </div>
                    <CardTitle className="text-xl mb-2">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-gray-600 mb-4 leading-relaxed">{feature.description}</p>
                    <div className="text-sm text-gray-500 font-medium bg-gray-50 rounded-full py-2 px-4 inline-block">
                      {feature.count}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Inline Ad after features */}
        {/* <InlineAd /> */}

        <div className="mt-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Indian Codes Hub?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We provide the most comprehensive and user-friendly platform for finding Indian codes with advanced features.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Search className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-3">Fast & Accurate Search</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Instant search results with intelligent filtering and suggestions. Find exactly what you need in seconds.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <CreditCard className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-3">Complete Database</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Comprehensive collection of all types of Indian codes with detailed information and regular updates.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Smartphone className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-3">One-Click Copy</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Copy any code, address, or information with a single click. Perfect for forms and applications.
              </p>
            </div>
          </div>
        </div>

        {/* SEO Content */}
        <div className="mt-16 prose prose-gray max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">About Indian Codes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-gray-600">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Essential Indian Codes</h3>
              <p className="text-sm leading-relaxed mb-4">
                India uses various standardized codes for different purposes. PIN codes for postal services, 
                IFSC codes for banking, STD codes for telecommunications, and RTO codes for vehicle registration 
                are among the most commonly used codes in daily life.
              </p>
              <p className="text-sm leading-relaxed">
                Our platform provides instant access to all these codes with search functionality and 
                copy features to make your work easier and more efficient.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">How to Use Indian Codes Hub</h3>
              <p className="text-sm leading-relaxed mb-4">
                Simply navigate to the specific code type you need, use our search functionality to find 
                the exact code, and copy it with one click. Our intelligent search suggests relevant 
                options as you type.
              </p>
              <p className="text-sm leading-relaxed">
                Each page is optimized for the specific code type with filters, suggestions, and 
                detailed information to help you find exactly what you're looking for.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}